import React, { useEffect, useMemo, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Input } from "./ui/input";

export const clientDisplayName = (c) =>
  (c.owner_type === "azienda" && c.owner_company_name)
    || [c.first_name, c.last_name].filter(Boolean).join(" ")
    || c.owner_company_name || c.email || c.phone || "Cliente";

const matches = (c, q) =>
  [c.first_name, c.last_name, c.owner_company_name, c.email, c.phone]
    .some((v) => (v || "").toLowerCase().includes(q));

export function OwnerClientSummary({ client, onChange, tid }) {
  return (
    <div className="border rounded-md bg-slate-50 p-3 text-sm space-y-0.5" data-testid={tid("client-summary")}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-slate-900" data-testid={tid("client-summary-name")}>{clientDisplayName(client)}</div>
        <button type="button" onClick={onChange} className="text-xs font-medium text-purple-700 hover:text-purple-900 shrink-0" data-testid={tid("client-change")}>
          Cambia cliente
        </button>
      </div>
      <div className="text-xs text-muted-foreground">{client.owner_type === "azienda" ? "Azienda" : "Privato"}</div>
      {client.owner_type === "azienda" && (client.first_name || client.last_name) && (
        <div className="text-xs text-slate-600">{[client.first_name, client.last_name].filter(Boolean).join(" ")}</div>
      )}
      {client.email && <div className="text-xs text-slate-600">{client.email}</div>}
      {client.phone && <div className="text-xs text-slate-600">{client.phone}</div>}
    </div>
  );
}

export function OwnerClientSearch({ proId, onPick, onCreateNew, tid }) {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/clients", { params: proId ? { professional_id: proId } : {} })
      .then((r) => setClients(r.data?.items || []))
      .catch(() => setClients([]));
  }, [proId]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (clients || []).filter((c) => !s || matches(c, s)).slice(0, 10);
  }, [clients, q]);

  return (
    <div className="relative" data-testid={tid("client-search")}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={clients === null ? "Caricamento clienti…" : "Cerca per nome, ragione sociale, email o telefono"}
          className="pl-8"
          data-testid={tid("client-search-input")}
        />
      </div>
      {open && clients !== null && (
        <div className="mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-white shadow-sm" data-testid={tid("client-search-results")}>
          {results.length ? results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { onPick(c); setOpen(false); }}
              className="block w-full text-left px-3 py-2 hover:bg-purple-50 border-b last:border-b-0"
              data-testid={tid(`client-option-${c.id}`)}
            >
              <div className="text-sm font-medium text-slate-900 truncate">{clientDisplayName(c)}</div>
              <div className="text-[11px] text-muted-foreground truncate">{[c.email, c.phone].filter(Boolean).join(" · ") || `${c.properties_count || 0} immobil${c.properties_count === 1 ? "e" : "i"}`}</div>
            </button>
          )) : (
            <div className="px-3 py-3 text-sm text-muted-foreground" data-testid={tid("client-search-empty")}>
              Nessun cliente trovato.{" "}
              <button type="button" onClick={onCreateNew} className="text-purple-700 font-medium hover:underline" data-testid={tid("client-create-new")}>
                Crea nuovo cliente
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
